<template>
  <section class="skin-founds">
    <p class="skin-title">{{ title || $t('Containers') }}</p>
    <div :class="{hm: cases.length > 1}" class="desk">
      <div :class="{ show: toggle }" class="skin-founds_wrap">
        <div v-for="(item, i) in cases" :key="i" class="item">
          <NuxtLink
              :to="isCollection ?
              getCollectionLink({
                name: item?.market_hash_name,
                slug: item?.link}) :
               getItemLinkWithExterior({
                name: item?.market_hash_name,
                slug: item?.link
              })"
              class="item_wrap"
          >
            <!-- /market-images + item.icon -->
            <img :alt="item.market_hash_name + ' ' + $t('Containers')"
                 :src="getImageFromWaxpeer(item?.market_hash_name || '', imageSizes || '100:100')
                  "
                 height="100"
                 loading="lazy"
                 width="100"
            />
            <p class="light">{{ item.market_hash_name }}</p>
          </NuxtLink>
        </div>
      </div>
      <div
          v-if="cases.length >= 4 && !toggle"
          :class="{ toggle: toggle }"
          class="more"
      >
        <button class="btn btn-primary" @click="toggle = !toggle">
          {{ $t(toggle ? 'show_less' : 'show_more') }}
        </button>
      </div>
    </div>
    <div v-if="cases.length > 1" class="skin-founds_wrap hd ht">
      <div class="carousel">
        <Swiper
            v-if="cases?.length"
            :grabCursor="true"
            :loop="false"
            :slides-per-view="1"
            @init="onSwiperInit"
        >
          <SwiperSlide v-for="(item, i) in cases" :key="item">
            <NuxtLink :to="getItemLinkWithExterior({
                name: item?.market_hash_name,
                slug: item?.link
            })" class="item_wrap">
              <!-- /market-images + item.icon -->
              <img :alt="item.market_hash_name + ' ' + $t('Containers')"
                   :src="getImageFromWaxpeer(item?.market_hash_name || '', imageSizes || '100:100')"
                   height="100"
                   loading="lazy"
                   width="100" />
              <p class="light">{{ item.market_hash_name }}</p>
            </NuxtLink>
          </SwiperSlide>
        </Swiper>
        <div
            :class="{disabled: carousel?.activeIndex === 0}"
            class="carousel-arrow arrow-prev"
            @click="prev"
        >
          <i
              class="i-arrow"
          ></i>
        </div>
        <div
            :class="{disabled: carousel?.isEnd}"
            class="carousel-arrow arrow-next"
            @click="next">
          <i
              class="i-arrow"
          ></i>
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import {useResize} from '~/composables/resize'
import {ref} from 'vue'
import {Swiper, SwiperSlide} from 'swiper/vue';
import 'swiper/swiper-bundle.css';
import type {Swiper as SwiperClass} from 'swiper';
import {getImageFromWaxpeer} from "~/utils/get-image-url";
import type {CratesItem} from "~/graphql/generated/schema-types";
import {getCollectionLink} from "~/utils/name-utils";

const props = defineProps<{
  title: string
  cases: CratesItem[]
  imageSizes?: string
  isCollection?: boolean
}>()

const currW = useResize()
const {locale} = useI18n()

const toggle = ref(false)
const carousel = ref<SwiperClass | null>(null);


const next = () => {
  if (carousel.value) {
    carousel.value.slideNext();
  }
};

const prev = () => {
  if (carousel.value) {
    carousel.value.slidePrev();
  }
};
const onSwiperInit = (e: any) => {
  if (!carousel.value) {
    carousel.value = e
  }
}
</script>

<style lang="scss" scoped>

@use '/assets/scss/mixins.scss' as *;
@use 'sass:color';
@use "/assets/scss/components/carousel";

h3 {
  margin-bottom: 1.6rem;
}

.skin-founds {
  width: 100%;

  &_wrap {
    padding-right: 0.3rem;

    .disabled {
      opacity: 0 !important;
    }

    .carousel {
      display: flex;
      align-items: center;

      .carousel-arrow {
        opacity: 1;
        cursor: pointer;

        &.disabled {
          opacity: 0.5;
          cursor: default;
        }
      }
    }
  }

  .item {
    border: 1px solid #252930;
    border-radius: .8rem;

    &_wrap {
      height: 15rem;
      padding: 2.2rem 1.7rem;
      font-size: 1.6rem;
      font-weight: 600;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;

      p {
        @include ellipsis;
        text-align: center;
        width: 100%;
      }
    }

    &:deep(img), img {
      height: 7rem;
      margin-bottom: 0.8rem;
    }
  }

  .more {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 15rem;
    width: 100%;
    padding: 1.6rem 4.8rem;
    display: flex;
    align-items: flex-end;
    justify-content: center;
    background: linear-gradient(to bottom, transparent, var(--accent-on-primary));

    .btn {
      width: 100%;
    }

    &.toggle {
      position: relative;
      height: 7rem;
    }
  }

  .mob {
    display: none;
  }
}

@media screen and (min-width: 761px) {
  .skin-founds {
    position: relative;
    overflow: hidden;

    &_wrap {
      overflow: hidden;
      max-height: 48rem;

      &.show {
        overflow-y: auto;
      }
    }

    .item {
      margin-bottom: 1rem;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
}

@media screen and (max-width: 760px) {
  .founds {
    border-bottom: 1px solid var(--surfaces-high-container);
    padding-bottom: 1.6rem;

    .desk {
      display: none;
    }

    .mob {
      display: block;
    }
  }
}
</style>
